import type {
  Event,
  EventAudienceMemberRow,
  EventSeries,
  GroupWithMemberCount,
  UserRole,
} from './database';
import type { ReminderTimeOption } from './notifications.types';

export const EVENT_DESCRIPTION_MAX_LENGTH = 15000;

/**
 * A group of events that occur on the same date
 */
export type EventGroup = {
  date: string;
  events: Event[];
};

/**
 * RSVP status type for event cards
 */
export type RsvpStatus = 'yes' | 'no' | 'attended' | null;

/**
 * Props for the presentational EventCard component
 */
export interface EventCardProps {
  event: Event;
  rsvpStatus: RsvpStatus;
  fileCount?: number;
  yGrow?: boolean;
  isPast?: boolean;
  onNavigate?: () => void;
  seriesInfo?: {
    position: number;
    total: number;
  };
}

/**
 * Props for the EventsList component
 */
export interface EventsListProps {
  events: Event[];
  userId: string | null;
  isPast?: boolean;
  fileCounts?: Map<number, number>;
  onEventNavigate?: (eventId: number) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

/**
 * Props for the EventForm component
 */
export interface EventFormProps {
  mode: 'add' | 'edit';
  eventId?: number;
}

/**
 * Recurrence types supported by the event form
 */
export type RecurrenceType = 'none' | 'weekly' | 'biweekly' | 'monthly';

/**
 * Values captured by the event creation/edit form
 */
export type EventFormValues = {
  title: string;
  description: string;
  location: string;
  eventDate: string;
  startTime: string;
  endTime?: string;
  eventType: string;
  rsvpDeadline?: string;
  capacity?: number;
  invitationGroups?: Set<UserRole>;
  eventGroupTargetIds?: Set<string>;
  eventLink?: string;
  linkName?: string;
  recurrenceType: RecurrenceType;
  recurrenceCount: number; // e.g., 4 for "4 weeks"
};

export type EventAudienceSectionMode = 'none' | 'all' | 'groups';

export type EventAudiencePreviewInput = {
  roleTargets: UserRole[];
  groupTargetIds: string[];
};

export type EventAudiencePreviewState = {
  members: EventAudienceMemberRow[];
  isLoading: boolean;
  error: Error | null;
};

export type EventAudienceMetadata = {
  roleTargets: UserRole[];
  groupTargets: GroupWithMemberCount[];
  resolvedMemberCount: number;
};

export type EventAudienceRoleSection = {
  role: UserRole;
  mode: EventAudienceSectionMode;
  groups: GroupWithMemberCount[];
  selectedGroupIds: string[];
  hasArchivedSelection: boolean;
};

export type InvitationGroupsRoleCountState = {
  memberCount: number | null;
  isLoading: boolean;
  isError: boolean;
};

export type InvitationGroupsSelectorProps = {
  selectedRoles: Set<UserRole>;
  selectedGroupIds: Set<string>;
  groups: GroupWithMemberCount[];
  isLoadingGroups: boolean;
  audienceMemberCount: number;
  isAudienceLoading: boolean;
  hasTargets: boolean;
  isPreviewDisabled: boolean;
  drillRole: UserRole | null;
  drillRoleMemberCount: InvitationGroupsRoleCountState;
  onDrillRoleChange: (role: UserRole | null) => void;
  onPreview: () => void;
  onRoleAdd: (role: UserRole) => void;
  onRoleSetAll: (role: UserRole) => void;
  onGroupToggle: (role: UserRole, groupId: string) => void;
  onRoleRemove: (role: UserRole) => void;
};

export type EventType = string;

/**
 * Props for the EventLink presentation component
 */
export interface EventLinkProps {
  /** The URL to link to */
  href: string;
  /** Optional display label for the link (defaults to 'Join Online') */
  label?: string;
}

/**
 * Props for the EventAlertBanner presentational component
 */
export interface EventAlertBannerProps {
  /** Type of alert to display */
  type: 'deadline' | 'capacity';
}

/**
 * Props for the EventHeader presentational component
 */
export interface EventHeaderProps {
  /** The event to display */
  event: Event;
  /** Whether the current user is an admin */
  isAdmin: boolean;
  /** Handler for edit button click */
  onEditClick: () => void;
  /** Whether the user has notifications enabled for this event */
  hasNotificationsEnabled: boolean;
  /** Optional props for series banner */
  seriesBanner?: {
    currentPosition: number;
    totalEvents: number;
    onBannerClick: () => void;
  };
}

/**
 * Props for the EventAdminRsvpCard presentational component
 */
export interface EventAdminRsvpCardProps {
  /** The event ID */
  eventId: number;
  /** Number of people going to the event */
  goingCount: number;
  /** Handler for viewing the RSVP list */
  onViewList: () => void;
  /** Whether the RSVP count is still loading */
  isLoading?: boolean;
}

/**
 * Props for the admin-only audience metadata component
 */
export interface EventAudienceMetadataCardProps {
  /** Admin-visible resolved event audience metadata */
  metadata?: EventAudienceMetadata;
  /** Whether metadata is still loading */
  isLoading: boolean;
  /** Error raised while loading metadata */
  error: Error | null;
}

/**
 * Props for the EventInfoSection presentational component
 */
export interface EventInfoSectionProps {
  /** The event to display */
  event: Event;
}

/**
 * Props for the EventReminderSelect presentational component
 */
export interface EventReminderSelectProps {
  /** Currently selected reminder options */
  selectedReminders: ReminderTimeOption[];
  /** Handler to toggle a reminder option */
  onToggle: (option: ReminderTimeOption) => void;
  /** Whether reminders are being updated */
  isUpdating: boolean;
}

/**
 * Props for the EventRsvpCard presentational component
 */
export interface EventRsvpCardProps {
  /** Current RSVP status ('yes' | 'no' | null) */
  currentStatus: 'yes' | 'no' | null;
  /** Whether RSVP deadline has passed */
  deadlinePassed: boolean;
  /** Whether event is at capacity */
  atCapacity: boolean;
  /** Formatted deadline text (optional) */
  deadlineText?: string;
  /** Whether mutation is in progress */
  isLoading: boolean;
  /**
   * Whether an admin has already recorded the user as attended. Only admins may
   * change an RSVP once it is 'attended', so the card locks instead of offering
   * an action the server would reject.
   */
  isAttended?: boolean;
  /** Handler for Going button click */
  onGoingClick: () => void;
  /** Handler for Not Going button click */
  onNotGoingClick: () => void;
  /** Whether this event is part of a series */
  isSeriesEvent?: boolean;
}

/**
 * Props for the RecurrenceSelector presentational component
 */
export interface RecurrenceSelectorProps {
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Currently selected recurrence type */
  recurrenceType: RecurrenceType;
  /** Number of occurrences */
  recurrenceCount: number;
  /** Event date (needed for preview dates) */
  eventDate: string;
  /** Start time (needed for preview dates) */
  startTime: string;
  /** Handler for recurrence type change */
  onRecurrenceTypeChange: (value: RecurrenceType) => void;
  /** Handler for recurrence count change */
  onRecurrenceCountChange: (value: number) => void;
  /** Handler for recurrence type blur */
  onRecurrenceTypeBlur?: () => void;
  /** Handler for recurrence count blur */
  onRecurrenceCountBlur?: () => void;
  /** Validation errors for recurrence type */
  recurrenceTypeErrors?: string[];
  /** Validation errors for recurrence count */
  recurrenceCountErrors?: string[];
  /** Whether to show validation errors */
  showErrors?: boolean;
}

/**
 * Event series record from the database
 */
export type { EventSeries };

/**
 * Props for the EventSeriesBanner presentational component
 */
export interface EventSeriesBannerProps {
  /** The series ID */
  seriesId: string;
  /** The current event ID */
  currentEventId: number;
  /** Handler for banner click */
  onClick: () => void;
}

/**
 * Props for the EventSeriesDrawer presentational component
 */
export interface EventSeriesDrawerProps {
  /** Controls whether the drawer is open */
  open: boolean;
  /** Callback when the drawer open state changes */
  onOpenChange: (open: boolean) => void;
  /** All events in the series */
  events: Event[];
  /** The current event ID */
  currentEventId: number;
  /** Handler for navigating to an event */
  onEventClick: (eventId: number) => void;
}
